import { useState } from "react";
import {
  useImportDtefFileMutation,
  useImportPixDtefFileMutation,
} from "@/services/conciliation/dtfe-import";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { toast } from "components/ui/use-toast";
import { MESSAGES, MESSAGES_PIX } from "../utils/messages";

export function useDtefImportFlow() {
  const { handleModal } = useDialogModal();

  const [importDtefArchive, { isLoading: isLoadingDtef }] =
    useImportDtefFileMutation();
  const [importPixArchive, { isLoading: isLoadingPix }] =
    useImportPixDtefFileMutation();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importType, setImportType] = useState<string>("");

  const isLoading = isLoadingDtef || isLoadingPix;

  const closeModal = () => {
    handleModal({ isOpen: false, element: null, title: "" });
  };

  const getMessages = () =>
    importType === "pix" ? MESSAGES_PIX : MESSAGES;

  const getImportFn = () =>
    importType === "pix" ? importPixArchive : importDtefArchive;

  const executeUpload = async (action: "replace" | "append") => {
    if (!selectedFile || !importType) return;

    const messages = getMessages();

    try {
      const response = await getImportFn()({
        file: selectedFile,
        action,
      }).unwrap();

      toast({
        variant: "default",
        description: response.message || messages.CREATE.SUCCESS.description,
        duration: 5000,
      });
    } catch (error: any) {
      console.error("Upload failed:", error);
      toast({
        variant: "destructive",
        description:
          error?.data?.messages?.join("\n") ||
          error?.data?.message ||
          messages.CREATE.ERROR.description,
        duration: 5000,
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !importType) return;

    const messages = getMessages();

    try {
      const response = await getImportFn()({ file: selectedFile }).unwrap();

      if (response.requiresAction) {
        return {
          duplicateDetected: true,
          existingCount: response.existingCount || 0,
          sameFileImports: response.sameFileImports ?? [],
        };
      }

      toast({
        variant: "default",
        description: response.message || messages.CREATE.SUCCESS.description,
        duration: 5000,
      });
      return { duplicateDetected: false, existingCount: 0, sameFileImports: [] };
    } catch (error: any) {
      console.error("Upload failed:", error);
      toast({
        variant: "destructive",
        description:
          error?.data?.messages?.join("\n") ||
          error?.data?.message ||
          messages.CREATE.ERROR.description,
        duration: 5000,
      });
      return { duplicateDetected: false, existingCount: 0, sameFileImports: [] };
    }
  };

  const handleClearAll = () => {
    setSelectedFile(null);
    setImportType("");
  };

  return {
    selectedFile,
    setSelectedFile,
    importType,
    setImportType,
    isLoading,
    handleUpload,
    executeUpload,
    handleClearAll,
    closeModal,
    handleModal,
  };
}
